/**
 * QuickPracticeSheet
 *
 * Slide-up modal that displays an AI-generated micro-lesson for a specific grammar error.
 * Shows: explanation → examples → 2 exercises (fill_blank + choose_correct).
 *
 * Uses the Content + Wrapper pattern (like ErrorModal) for Storybook compatibility.
 */

import React, { useCallback, useState } from 'react';
import { Modal, ScrollView, TouchableOpacity, View, ActivityIndicator } from 'react-native';
import { Box, VStack, HStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { colors, FS_XS, FS_SM, FS_MD, FS_LG } from '../../theme';
import { FillOrChooseExercise } from '../exercises/FillOrChooseExercise';
import { resolveBilingualContent, type BilingualContent } from '../../types/lesson';

import { useUserPreferencesStore } from '../../store/userPreferencesStore';
import { selectLessonLanguage, selectSourceLocale, selectTargetLocale } from '../../store/userPreferencesSelectors';
import type { QuickPracticeContent, ExerciseProgress } from '../../types/lesson';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

// ─── Types ───────────────────────────────────────────────────────────

export interface QuickPracticeSheetProps {
  visible: boolean;
  loading: boolean;
  error: string | null;
  content: QuickPracticeContent | null;
  errorType: string;
  onClose: () => void;
  onRetry?: () => void;
}

// ─── Content (presentational) ────────────────────────────────────────

export const QuickPracticeContent_View = React.memo(function QuickPracticeContent_View({
  loading,
  error,
  content,
  errorType,
  onClose,
  onRetry,
}: Omit<QuickPracticeSheetProps, 'visible'>) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const lessonLanguage = useUserPreferencesStore(selectLessonLanguage);
  const sourceLocale = useUserPreferencesStore(selectSourceLocale);
  const targetLocale = useUserPreferencesStore(selectTargetLocale);

  const [completedExercises, setCompletedExercises] = useState<Set<string>>(new Set());

  const handleExerciseComplete = useCallback(
    (id: string, _type: string, _correct: boolean, _score: number, _extras?: Partial<ExerciseProgress>) => {
      setCompletedExercises((prev) => new Set(prev).add(id));
    },
    [],
  );

  const errorLabel = errorType.replace(/_/g, ' ');

  return (
    <SafeAreaView
      edges={['top']}
      style={{
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'flex-end',
      }}
    >
      <View
        testID="quick-practice-sheet"
        accessibilityViewIsModal
        style={{
          backgroundColor: theme.background,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '80%',
          minHeight: '40%',
        }}
      >
        {/* Header */}
        <HStack
          alignItems="center"
          justifyContent="space-between"
          px="$4"
          py="$3"
          style={{ borderBottomWidth: 1, borderBottomColor: theme.border }}
        >
          <VStack>
            <Text
              allowFontScaling={false}
              accessibilityRole="header"
              style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
            >
              {t('chat.quickPractice.title')}
            </Text>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, textTransform: 'capitalize', ...ffs }}
            >
              {errorLabel}
            </Text>
          </VStack>
          <TouchableOpacity
            testID="quick-practice-close"
            accessibilityLabel={t('common.close')}
            accessibilityRole="button"
            onPress={onClose}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_LG * fontScale, color: theme.textSecondary, fontWeight: '300' }}
            >
              {'\u2715'}
            </Text>
          </TouchableOpacity>
        </HStack>

        {/* Body */}
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: 16, paddingBottom: 32 }}
          showsVerticalScrollIndicator={false}
        >
          {loading && (
            <VStack space="md" alignItems="center" py="$8">
              <ActivityIndicator size="large" color={colors.primary} />
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
              >
                {t('chat.quickPractice.loading')}
              </Text>
            </VStack>
          )}

          {error && (
            <VStack space="md" alignItems="center" py="$8">
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: colors.error, textAlign: 'center', ...ffs }}
              >
                {error}
              </Text>
              {onRetry && (
                <TouchableOpacity
                  testID="quick-practice-retry"
                  accessibilityLabel={t('common.retry')}
                  accessibilityRole="button"
                  onPress={onRetry}
                >
                  <Box
                    style={{ backgroundColor: theme.primarySoft, borderColor: theme.primary }}
                    borderWidth={1}
                    px="$4"
                    py="$2"
                    rounded="$lg"
                  >
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: FS_SM * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}
                    >
                      {t('common.retry')}
                    </Text>
                  </Box>
                </TouchableOpacity>
              )}
            </VStack>
          )}

          {content && !loading && (
            <VStack space="lg" testID="quick-practice-content">
              {/* Explanation Section */}
              <VStack space="sm">
                <Text
                  allowFontScaling={false}
                  accessibilityRole="header"
                  style={{ fontSize: FS_SM * fontScale, fontWeight: '700', color: theme.primary, textTransform: 'uppercase', ...ffs }}
                >
                  {t('chat.quickPractice.explanation')}
                </Text>
                <Box
                  style={{ backgroundColor: theme.primarySoft, borderColor: theme.primary }}
                  borderWidth={1}
                  p="$3"
                  rounded="$lg"
                >
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: FS_SM * fontScale, color: theme.text, lineHeight: Math.round(FS_SM * fontScale * 1.6), ...ffs }}
                  >
                    {resolveBilingualContent(content.explanation, lessonLanguage, sourceLocale, targetLocale)}
                  </Text>
                </Box>
              </VStack>

              {/* Examples Section */}
              {content.examples.length > 0 && (
                <VStack space="sm">
                  <Text
                    allowFontScaling={false}
                    accessibilityRole="header"
                    style={{ fontSize: FS_SM * fontScale, fontWeight: '700', color: theme.primary, textTransform: 'uppercase', ...ffs }}
                  >
                    {t('chat.quickPractice.examples')}
                  </Text>
                  <VStack space="xs">
                    {content.examples.map((example, idx) => {
                      const nativeText = typeof example.native === 'string'
                        ? example.native
                        : resolveBilingualContent(example.native as BilingualContent, 'source', sourceLocale, targetLocale);
                      const noteText = example.note
                        ? resolveBilingualContent(example.note, lessonLanguage, sourceLocale, targetLocale)
                        : null;

                      return (
                        <Box
                          key={idx}
                          style={{ backgroundColor: theme.card, borderColor: theme.border }}
                          borderWidth={1}
                          p="$3"
                          rounded="$md"
                        >
                          <VStack space="xs">
                            <Text
                              allowFontScaling={false}
                              style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
                            >
                              {nativeText}
                            </Text>
                            <Text
                              allowFontScaling={false}
                              style={{ fontSize: FS_SM * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
                            >
                              {example.target}
                            </Text>
                            {noteText && (
                              <Text
                                allowFontScaling={false}
                                style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
                              >
                                {noteText}
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </VStack>
              )}

              {/* Exercises Section */}
              {content.exercises.length > 0 && (
                <VStack space="sm">
                  <Text
                    allowFontScaling={false}
                    accessibilityRole="header"
                    style={{ fontSize: FS_SM * fontScale, fontWeight: '700', color: theme.primary, textTransform: 'uppercase', ...ffs }}
                  >
                    {t('chat.quickPractice.exercises')}
                  </Text>
                  <VStack space="sm">
                    {content.exercises.map((exercise, idx) => {
                      const exerciseId = `qp-${idx}`;
                      const resolvedPrompt = resolveBilingualContent(
                        exercise.prompt,
                        lessonLanguage,
                        sourceLocale,
                        targetLocale,
                      );
                      const resolvedHint = exercise.hint
                        ? resolveBilingualContent(exercise.hint, lessonLanguage, sourceLocale, targetLocale)
                        : undefined;

                      return (
                        <FillOrChooseExercise
                          key={exerciseId}
                          exerciseId={exerciseId}
                          type={exercise.type as 'fill_blank' | 'choose_correct' | 'translate'}
                          prompt={resolvedPrompt}
                          correctAnswer={exercise.correctAnswer}
                          acceptableAnswers={exercise.acceptableAnswers}
                          options={exercise.options}
                          hint={resolvedHint}
                          index={idx + 1}
                          isCompleted={completedExercises.has(exerciseId)}
                          onComplete={handleExerciseComplete}
                        />
                      );
                    })}
                  </VStack>
                </VStack>
              )}
            </VStack>
          )}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
});

// ─── Modal Wrapper ───────────────────────────────────────────────────

export function QuickPracticeSheet({
  visible,
  loading,
  error,
  content,
  errorType,
  onClose,
  onRetry,
}: QuickPracticeSheetProps) {
  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <QuickPracticeContent_View
        loading={loading}
        error={error}
        content={content}
        errorType={errorType}
        onClose={onClose}
        onRetry={onRetry}
      />
    </Modal>
  );
}
